
export type ShiftType = 'WORK' | 'OFF' | 'NO_SHOW';

export type ExpenseCategory = 'FOOD' | 'FUEL' | 'TRANSPORT' | 'ENTERTAINMENT' | 'FIXED' | 'OTHER';
export type ExtraIncomeCategory = 'UBER' | '99' | 'INDRIVE' | 'TAXI' | 'SALES' | 'OTHER';

export type TransactionOrigin = 'RHINO' | 'THIRD_PARTY' | 'SALES' | 'PERSONAL';

export interface ExpenseItem {
  id: string;
  category: ExpenseCategory;
  origin: TransactionOrigin;
  amount: number;
  description?: string;
}

export interface ExtraEarningsItem {
  id: string;
  category: ExtraIncomeCategory;
  origin: TransactionOrigin;
  amount: number;
  description?: string;
}

export interface BilingualEvent {
  start: string;
  end: string;
}

export interface Shift {
  id: string;
  user_id: string;
  type: ShiftType;
  noShow: boolean; // Maps to DB 'no_show'
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  isHoliday: boolean;
  bilingualStart?: string; // HH:mm
  bilingualEnd?: string; // HH:mm
  totalEarnings: number; // Rhino Earnings
  totalHours: number;
  appTips: number;
  tips: number;
  outsideTips?: number;
  breakdown: {
    basePay: number;
    nightPay: number;
    bilingualPay: number;
  };
  expenses: ExpenseItem[];
  extraEarnings: ExtraEarningsItem[];
  unpaidBreakMinutes: number;
  plannedStartTime?: string; // HH:mm — original scheduled start
  plannedEndTime?: string;   // HH:mm — original scheduled end
  vehiclePlate?: string;
  carReadyOnTime?: boolean;
  carArrivalTime?: string;
  loginAppTime?: string;
  loginTelegramTime?: string;
  logoutTelegramTime?: string;
  generation?: 'OLD' | 'NEW';
}

export interface ShiftInput {
  id?: string;
  type: ShiftType;
  date: string;
  startTime: string;
  endTime: string;
  isHoliday: boolean;
  hasBilingual: boolean;
  bilingualStart: string;
  bilingualEnd: string;
  appTips: string;
  tips: string;
  outsideTips?: string;
  expenses: ExpenseItem[];
  extraEarnings: ExtraEarningsItem[];
  unpaidBreakMinutes: string;
  vehiclePlate: string;
  carReadyOnTime?: boolean;
  carArrivalTime?: string;
  loginAppTime?: string;
  loginTelegramTime?: string;
}

export interface UserProfile {
  id: string;
  nickname: string;
  phone?: string;
  avatar_url?: string;
  xp_points: number;
  tier: string;
  created_at?: string;
  updated_at?: string;
  alarm_sound?: string;
  rhino_score?: number;
  rhino_tier?: 'Ouro' | 'Prata' | 'Bronze';
  rhino_events?: Array<{
    date: string;
    description: string;
    score: number;
    type: 'multa' | 'comportamento' | 'aviso' | 'elogio' | 'regular';
  }>;
  rhino_last_update?: string;
  email?: string;
  is_premium?: boolean;
  subscription_id?: string;
  subscription_end_date?: string;
  mp_customer_id?: string;
  instagram_user?: string;
  youtube_channel?: string;
  tiktok_user?: string;
  linkedin_user?: string;
  x_user?: string;
  facebook_user?: string;
  show_socials?: boolean;
  privacy_hide_stats?: boolean;
  privacy_hide_avatar?: boolean;
  is_founder?: boolean;
  tracked_friends?: string[];
  generation?: 'OLD' | 'NEW';
  generation_selected?: boolean;
  last_avatar_gen_at?: string;
  is_bilingual_active?: boolean;
  referral_code?: string;
}

export enum ViewMode {
  DASHBOARD = 'DASHBOARD',
  ADD_SHIFT = 'ADD_SHIFT',
  SCAN_SHIFT = 'SCAN_SHIFT',
  HISTORY = 'HISTORY',
  SETTINGS = 'SETTINGS',
  FEEDBACK = 'FEEDBACK',
  PROFILE = 'PROFILE',
  RANKING = 'RANKING',
  FINANCE = 'FINANCE',
  ADVANCE = 'ADVANCE',
  PERFORMANCE = 'PERFORMANCE',
  SUPPORT_CENTER = 'SUPPORT_CENTER',
  FLEET = 'FLEET',
  AI_AVATAR = 'AI_AVATAR',
  GARAGE = 'GARAGE',
  PARTNERS = 'PARTNERS'
}

export interface Car {
  id?: number;
  placa: string;
  modelo: string;
  endereco: string;
  lat: number;
  lon: number;
  created_at?: string;
}

export interface CarRequest extends Car {
  status: 'pending' | 'approved' | 'rejected';
  user_email?: string;
}

export interface Story {
  id: string;
  media_url: string;
  link_url?: string;
  contact_phone?: string;
  expires_at: string;
  status: 'active' | 'sold' | 'inactive';
  title?: string;
  user_id?: string;
  created_at?: string;
}

export type GarageCheckinStatus = 'WAITING' | 'CAR_READY' | 'DRIVER_OK' | 'COMPLETED';
export type AuditColor = 'GRAY' | 'GREEN' | 'YELLOW' | 'RED' | 'BLUE';

export interface GarageCheckin {
  id: string;
  user_id: string;
  shift_date: string;
  scheduled_start: string;
  vehicle_plate: string;
  status: GarageCheckinStatus;
  audit_color: AuditColor;
  car_arrived_at?: string;
  is_bilingual_start?: boolean;
  agent_id?: string;
  driver_confirmed_at?: string;
  login_telegram_at?: string;
  login_app_at?: string;
  token_delay?: boolean;
  token_proof_url?: string;
  car_swapped?: boolean;
  swap_new_plate?: string;
  exception_note?: string;
  agent_validated?: boolean;
  agent_validated_at?: string;
  validated_by?: string;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
  // Joined fields
  driver_nickname?: string;
  driver_avatar?: string;
  driver_tier?: string;
}

export interface GarageAgent {
  id: string;
  name: string;
  pin: string;
  is_active: boolean;
  created_at?: string;
}
